/* ════ INBOX (per-user ideas backlog) ══════════════════════ */
import { state } from './state.js';
import { $ } from './dom.js';
import { MO } from './config.js';
import { catClass, catVars, catIcon, catLabel, isBuiltin } from './categories.js';
import { sb } from './supabase/client.js';
import { setSyncDot } from './supabase/data.js';
import { showToast } from './utils/toast.js';
import { openIdeaModal, openIdeaEdit } from './modals/idea.js';
import { scheduleFromIdea } from './modals/task.js';
import { setView } from './views.js';
import { displayName } from './utils/name.js';
import { fdate, addD } from './utils/date.js';
import { canSeeIdea } from './privacy.js';

const esc = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const IMP_RANK = { high: 0, normal: 1, low: 2 };
let todayISO = '';   // refreshed each render, used for overdue styling
const fmtDue = s => { const [y, m, d] = s.split('-').map(Number); return `${d} ${MO[m - 1]}`; };
const nameFor = email => state.resources.find(r => r.user_email === email)?.name
  || state.users.find(u => u.email === email)?.name || displayName(email);

let selectMode = false, dragId = null;
const selected = new Set();

export function updateRainyBadge() {
  const me = state.currentUser?.email || '';
  // Inbox badge = my own cards.
  const b = $('rainy-badge');
  if (b) { const n = state.ideas.filter(i => i.created_by === me).length; b.textContent = n; b.style.display = n > 0 ? 'inline-flex' : 'none'; }
  // Separate badge = cards others have flagged for me to review.
  const f = $('flag-badge');
  if (f) { const n = state.ideas.filter(i => i.created_by !== me && (i.mentions || []).includes(me)).length; f.textContent = n; f.style.display = n > 0 ? 'inline-flex' : 'none'; }
}

// On first load after sign-in, default the Inbox to "Flagged for me" when there
// are cards waiting on the user (only once — never overrides a later choice).
let inboxDefaultApplied = false;
export function applyInboxDefault() {
  if (inboxDefaultApplied) return;
  inboxDefaultApplied = true;
  const me = state.currentUser?.email || '';
  if (me && state.ideas.some(i => i.created_by !== me && (i.mentions || []).includes(me))) state.inboxUser = '@me';
}

// Manual reordering removed — the other sorts override it anyway.
const reorderable = () => false;

function populateUserFilter() {
  const sel = $('inbox-user'); if (!sel) return;
  const me = state.currentUser?.email || '';
  if (!state.inboxUser) state.inboxUser = me || 'all';
  // Everyone who's registered, mapped to a resource, or has created anything.
  const all = [...state.users.map(u => u.email), ...state.resources.map(r => r.user_email),
    ...state.ideas.map(i => i.created_by), ...state.tasks.map(t => t.created_by)];
  const others = [...new Set(all)].filter(u => u && u !== me).sort();
  const flaggedForMe = me && state.ideas.some(i => (i.mentions || []).includes(me));
  sel.innerHTML =
    (me ? `<option value="${esc(me)}">My inbox (${esc(nameFor(me))})</option>` : '') +
    (flaggedForMe ? `<option value="@me">🔔 Flagged for me</option>` : '') +
    others.map(u => `<option value="${esc(u)}">${esc(nameFor(u))}</option>`).join('') +
    `<option value="all">All users</option>`;
  const valid = [me, '@me', ...others, 'all'].filter(Boolean);
  if (!valid.includes(state.inboxUser)) state.inboxUser = me || 'all';
  sel.value = state.inboxUser;
}

function visibleIdeas() {
  let list = state.ideas.slice();
  const me = state.currentUser?.email || '';
  if (state.inboxUser === '@me') list = list.filter(i => (i.mentions || []).includes(me));
  else if (state.inboxUser && state.inboxUser !== 'all') list = list.filter(i => i.created_by === state.inboxUser);
  if (state.inboxTag !== 'all') list = list.filter(i => (i.tags || []).includes(state.inboxTag));
  if (state.inboxDue !== 'all') {
    const t0 = new Date(); t0.setHours(0, 0, 0, 0);
    const today = fdate(t0), week = fdate(addD(t0, 7)), month = fdate(addD(t0, 30));
    list = list.filter(i => {
      const d = i.due;
      switch (state.inboxDue) {
        case 'overdue': return d && d < today;
        case 'week': return d && d >= today && d <= week;
        case 'month': return d && d >= today && d <= month;
        case 'has': return !!d;
        case 'none': return !d;
        default: return true;
      }
    });
  }
  const byNew = (a, b) => String(b.created_at || '').localeCompare(String(a.created_at || ''));
  if (state.inboxSort === 'importance') list.sort((a, b) => (IMP_RANK[a.importance] ?? 1) - (IMP_RANK[b.importance] ?? 1) || byNew(a, b));
  else if (state.inboxSort === 'due') list.sort((a, b) => (a.due || '9999').localeCompare(b.due || '9999') || byNew(a, b));
  else if (state.inboxSort === 'manual') list.sort((a, b) => (a.sort ?? Infinity) - (b.sort ?? Infinity) || byNew(a, b));
  else list.sort(byNew);
  return list;
}

function populateTagFilter() {
  const sel = $('inbox-tag'); if (!sel) return;
  const tags = [...new Set(state.ideas.flatMap(i => i.tags || []))].sort();
  const cur = state.inboxTag;
  sel.innerHTML = '<option value="all">All tags</option>' + tags.map(t => `<option value="${esc(t)}">${esc(t)}</option>`).join('');
  sel.value = tags.includes(cur) ? cur : 'all';
  if (sel.value !== cur) state.inboxTag = sel.value;
}

function buildCard(idea) {
  const stl = idea.subtasks || [], total = stl.length, done = stl.filter(s => s.done).length;
  const card = document.createElement('div');
  card.className = `idea-card ${catClass(idea.cat)}` + (selected.has(idea.id) ? ' sel' : '');
  card.style.cssText = catVars(idea.cat);   // custom-category colour vars (empty for built-ins)
  const badgeCls = isBuiltin(idea.cat) ? idea.cat : 'cat-custom';
  const catBadge = `<div class="idea-cat-badge ${badgeCls}">${catIcon(idea.cat)} ${esc(catLabel(idea.cat))}</div>`;
  // Private cards are redacted for everyone but the owner & assigned resource.
  if (!canSeeIdea(idea)) {
    card.classList.add('private');
    card.onclick = () => { if (selectMode) toggleSelect(idea.id); };
    card.innerHTML = `
      <div class="idea-check"></div>
      <div class="idea-top">
        ${catBadge}
        <span class="idea-lock-ic" title="Private">🔒</span>
      </div>
      <div class="idea-title idea-private-title">🔒 Private</div>
      <div class="idea-footer">
        <div class="idea-meta">${idea.created_by ? 'Added by ' + esc(nameFor(idea.created_by)) : ''}</div>
      </div>`;
    return card;
  }
  card.onclick = () => { if (selectMode) toggleSelect(idea.id); else openIdeaEdit(idea.id); };
  const resName = state.resources.find(r => r.id === idea.resource)?.name || '';
  const imp = idea.importance || 'normal';
  card.innerHTML = `
      <div class="idea-check"></div>
      <div class="idea-top">
        ${catBadge}
        ${idea.private ? '<span class="idea-lock-ic" title="Private">🔒 Private</span>' : ''}
        ${idea.locked ? '<span class="idea-lock-ic" title="Locked">🔒</span>' : ''}
        ${imp !== 'normal' ? `<span class="imp-tag imp-${imp}">${imp === 'high' ? '! High' : '↓ Low'}</span>` : ''}
      </div>
      <div class="idea-title">${esc(idea.name)}</div>
      ${idea.description ? `<div class="idea-desc">${esc(idea.description)}</div>` : ''}
      ${(idea.mentions || []).length ? `<div class="idea-mentions">${idea.mentions.map(e => `<span class="mention-chip sm">🔔 ${esc(nameFor(e))}</span>`).join('')}</div>` : ''}
      ${(idea.tags || []).length ? `<div class="card-tags">${idea.tags.map(t => `<span class="card-tag">${esc(t)}</span>`).join('')}</div>` : ''}
      <div class="idea-tags">
        ${resName ? `<span class="idea-res-tag">${esc(resName)}</span>` : ''}
        ${idea.days ? `<span class="idea-days-tag">${idea.days}d</span>` : ''}
        ${idea.due ? `<span class="idea-due-tag${idea.due < todayISO ? ' overdue' : ''}">📅 ${fmtDue(idea.due)}</span>` : ''}
        ${total > 0 ? `<span class="idea-st-count${done === total ? ' has-done' : ''}">&#10003; ${done}/${total}</span>` : ''}
      </div>
      <div class="idea-footer">
        <div class="idea-meta">${idea.created_by ? 'Added by ' + esc(nameFor(idea.created_by)) : ''}</div>
        <button class="idea-sched" data-id="${idea.id}">Schedule →</button>
      </div>`;
  card.querySelector('.idea-sched').addEventListener('click', e => {
    e.stopPropagation(); if (selectMode) return;
    setView('gantt'); scheduleFromIdea(idea);
  });
  if (reorderable()) wireDrag(card, idea.id);
  return card;
}

// Group cards under tag headers (cards with multiple tags appear under each).
function renderGrouped(grid, list) {
  const groups = new Map();
  list.forEach(idea => {
    const tags = (idea.tags || []).length ? idea.tags : ['— Untagged —'];
    tags.forEach(tg => { if (!groups.has(tg)) groups.set(tg, []); groups.get(tg).push(idea); });
  });
  const names = [...groups.keys()].sort((a, b) =>
    a === '— Untagged —' ? 1 : b === '— Untagged —' ? -1 : a.localeCompare(b));
  names.forEach(tg => {
    const h = document.createElement('div'); h.className = 'inbox-group-hdr';
    h.textContent = `${tg}  (${groups.get(tg).length})`;
    grid.appendChild(h);
    groups.get(tg).forEach(idea => grid.appendChild(buildCard(idea)));
  });
}

export function renderIdeas() {
  const t0 = new Date(); t0.setHours(0, 0, 0, 0); todayISO = fdate(t0);
  populateUserFilter();
  populateTagFilter();
  const grid = $('ideas-grid'); grid.innerHTML = '';
  grid.classList.toggle('selecting', selectMode);
  grid.classList.toggle('reorderable', reorderable());
  grid.classList.toggle('grouped', state.inboxSort === 'tag');
  [...selected].forEach(id => { if (!state.ideas.find(i => i.id === id)) selected.delete(id); });
  const list = visibleIdeas();
  if (state.inboxSort === 'tag') renderGrouped(grid, list);
  else list.forEach(idea => grid.appendChild(buildCard(idea)));
  if (!selectMode) {
    const add = document.createElement('div'); add.className = 'add-idea-card'; add.onclick = openIdeaModal;
    add.innerHTML = `<div class="add-idea-icon">+</div><div class="add-idea-label">Add idea</div>`;
    grid.appendChild(add);
  }
}

/* ── Drag to reorder (Manual sort) ───────────────────────── */
function wireDrag(card, id) {
  card.draggable = true;
  card.addEventListener('dragstart', e => { dragId = id; card.classList.add('dragging-card'); e.dataTransfer.effectAllowed = 'move'; });
  card.addEventListener('dragend', () => { dragId = null; card.classList.remove('dragging-card'); });
  card.addEventListener('dragover', e => { e.preventDefault(); });
  card.addEventListener('drop', e => { e.preventDefault(); if (dragId && dragId !== id) reorder(dragId, id); });
}

async function reorder(fromId, toId) {
  const order = visibleIdeas();
  const from = order.findIndex(i => i.id === fromId), to = order.findIndex(i => i.id === toId);
  if (from < 0 || to < 0 || from === to) return;
  const [moved] = order.splice(from, 1); order.splice(to, 0, moved);
  order.forEach((i, idx) => { i.sort = idx; });   // same refs as state.ideas
  renderIdeas();
  const lo = Math.min(from, to), hi = Math.max(from, to);
  setSyncDot('busy');
  const res = await Promise.all(order.slice(lo, hi + 1).map(i => sb.from('ideas').update({ sort: i.sort }).eq('id', i.id)));
  if (res.find(r => r.error)) { setSyncDot('err'); showToast('Reorder failed'); } else setSyncDot('ok');
}

/* ── Bulk select + edit ──────────────────────────────────── */
function toggleSelect(id) { if (selected.has(id)) selected.delete(id); else selected.add(id); renderIdeas(); updateBulkBar(); }
function updateBulkBar() {
  $('bulk-count').textContent = `${selected.size} selected`;
  const allSel = state.ideas.length > 0 && selected.size === state.ideas.length;
  $('bulk-selall').textContent = allSel ? 'Clear all' : 'Select all';
}
function selectAll() {
  if (state.ideas.length > 0 && selected.size === state.ideas.length) selected.clear();
  else visibleIdeas().forEach(i => selected.add(i.id));
  renderIdeas(); updateBulkBar();
}
function populateBulkRes() {
  $('bulk-res').innerHTML = '<option value="">Resource…</option>' +
    state.resources.map(r => `<option value="${r.id}">${r.name}</option>`).join('');
  const me = state.currentUser?.email || '';
  const emails = [...new Set([...state.users.map(u => u.email), ...state.resources.map(r => r.user_email),
    ...state.ideas.map(i => i.created_by), ...state.tasks.map(t => t.created_by), me].filter(Boolean))].sort();
  $('bulk-owner').innerHTML = '<option value="">Move to inbox…</option>' +
    emails.map(e => `<option value="${e}">${esc(nameFor(e))}${e === me ? ' (me)' : ''}</option>`).join('');
}
function setSelectMode(on) {
  selectMode = on;
  if (!on) selected.clear();
  $('btn-select-toggle').classList.toggle('on', on);
  $('btn-select-toggle').textContent = on ? 'Done' : 'Select';
  $('bulk-bar').style.display = on ? 'flex' : 'none';
  if (on) populateBulkRes();
  updateBulkBar(); renderIdeas();
}
async function applyBulk() {
  if (selected.size === 0) { showToast('Select some cards first'); return; }
  const patch = {};
  const c = $('bulk-cat').value, im = $('bulk-imp').value, r = $('bulk-res').value, o = $('bulk-owner').value;
  if (c) patch.cat = c;
  if (im) patch.importance = im;
  if (r) patch.resource = r;
  if (o) patch.created_by = o;
  if (Object.keys(patch).length === 0) { showToast('Pick a field to change'); return; }
  setSyncDot('busy');
  const { error } = await sb.from('ideas').update(patch).in('id', [...selected]);
  if (error) { setSyncDot('err'); showToast('Update failed: ' + error.message); return; }
  setSyncDot('ok'); showToast(`Updated ${selected.size} ideas ✓`);
  ['bulk-cat', 'bulk-imp', 'bulk-res', 'bulk-owner'].forEach(id => $(id).value = '');
  setSelectMode(false);
}
async function deleteBulk() {
  if (selected.size === 0) { showToast('Select some cards first'); return; }
  // Locked cards are protected from bulk delete.
  const lockedIds = new Set(state.ideas.filter(i => i.locked).map(i => i.id));
  const targets = [...selected].filter(id => !lockedIds.has(id));
  const skipped = selected.size - targets.length;
  if (targets.length === 0) { showToast('🔒 Selected card(s) are locked'); return; }
  if (!confirm(`Delete ${targets.length} idea(s)?${skipped ? ` (${skipped} locked, kept)` : ''}`)) return;
  setSyncDot('busy');
  const { error } = await sb.from('ideas').delete().in('id', targets);
  if (error) { setSyncDot('err'); showToast('Delete failed: ' + error.message); return; }
  setSyncDot('ok'); showToast(skipped ? `Deleted ${targets.length}, kept ${skipped} locked` : 'Ideas deleted');
  setSelectMode(false);
}

export function initIdeas() {
  $('btn-select-toggle').addEventListener('click', () => setSelectMode(!selectMode));
  $('bulk-selall').addEventListener('click', selectAll);
  $('bulk-apply').addEventListener('click', applyBulk);
  $('bulk-delete').addEventListener('click', deleteBulk);
  $('bulk-cancel').addEventListener('click', () => setSelectMode(false));
  $('inbox-user').addEventListener('change', e => { state.inboxUser = e.target.value; renderIdeas(); });
  $('inbox-sort').addEventListener('change', e => { state.inboxSort = e.target.value; renderIdeas(); });
  $('inbox-tag').addEventListener('change', e => { state.inboxTag = e.target.value; renderIdeas(); });
  $('inbox-due').addEventListener('change', e => { state.inboxDue = e.target.value; renderIdeas(); });
}
